var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { Button, Drawer, Flex, Radio, Spin, Typography, theme, } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faXmark, } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPOSER_DRAWER_WIDTH } from '../../../constants/drawerSizes';
import { SuggestionSection } from './components/SuggestionSection';
/** Initially visible counts per section; "Load more" reveals the rest. */
var INITIAL_AVAILABLE_COUNT = 4;
var INITIAL_CONFLICTS_COUNT = 2;
/**
 * Drawer that lists suggested meeting times, grouped by attendee availability.
 * Presentational: the suggestions are pre-fetched by
 * {@link TimeSuggestionsContainer} and passed in via `outcome`. Each section is
 * capped initially; "Load more" reveals the remaining results.
 */
export var TimeSuggestions = function (_a) {
    var outcome = _a.outcome, onApply = _a.onApply, onClose = _a.onClose;
    var t = useTranslation('meetingRooms').t;
    var token = theme.useToken().token;
    var _b = __read(useState(), 2), selectedId = _b[0], setSelectedId = _b[1];
    var _c = __read(useState(false), 2), showAll = _c[0], setShowAll = _c[1];
    var renderBody = function () {
        if (outcome.status === 'loading') {
            return (_jsxs(CenteredState, { children: [_jsx(Spin, {}), _jsx(Typography.Text, { style: { color: token.colorTextSecondary }, children: t('time_suggestions.loading') })] }));
        }
        // The entry button is always available, so the drawer can be opened with no
        // results (empty / skipped all-day or recurring / error / not yet fetched).
        // Show a neutral empty state rather than a blank drawer.
        if (outcome.status !== 'success') {
            return (_jsxs(CenteredState, { children: [_jsx(Typography.Title, { level: 5, style: { margin: 0 }, children: t('time_suggestions.empty_title') }), _jsx(Typography.Text, { style: { color: token.colorTextSecondary }, children: t('time_suggestions.empty_description') })] }));
        }
        var visibleAvailable = showAll
            ? outcome.available
            : outcome.available.slice(0, INITIAL_AVAILABLE_COUNT);
        var visibleConflicts = showAll
            ? outcome.conflicts
            : outcome.conflicts.slice(0, INITIAL_CONFLICTS_COUNT);
        var hasMore = outcome.available.length > INITIAL_AVAILABLE_COUNT ||
            outcome.conflicts.length > INITIAL_CONFLICTS_COUNT;
        return (_jsx(Radio.Group, { value: selectedId, onChange: function (e) {
                var _a;
                var id = e.target.value;
                setSelectedId(id);
                var suggestion = (_a = outcome.available.find(function (s) { return s.id === id; })) !== null && _a !== void 0 ? _a : outcome.conflicts.find(function (s) { return s.id === id; });
                if (suggestion) {
                    onApply(suggestion);
                }
            }, style: { width: '100%' }, children: _jsxs(Flex, { vertical: true, gap: 16, style: { padding: 16, overflowY: 'auto', flex: 1 }, children: [_jsx(PartialBanner, { partial: outcome.partial, token: token }), visibleAvailable.length > 0 && (_jsx(SuggestionSection, { token: token, label: t('time_suggestions.everyone_available'), suggestions: visibleAvailable, selectedId: selectedId })), visibleConflicts.length > 0 && (_jsx(SuggestionSection, { token: token, label: t('time_suggestions.some_conflicts'), suggestions: visibleConflicts, selectedId: selectedId })), !showAll && hasMore && (_jsx(Button, { onClick: function () { return setShowAll(true); }, children: t('time_suggestions.load_more') }))] }) }));
    };
    return (_jsx(StyledDrawer, { title: _jsxs(Flex, { align: "center", gap: 4, style: { width: '100%' }, children: [_jsx(Button, { onClick: onClose, type: "text", icon: _jsx(FontAwesomeIcon, { icon: faAngleLeft }) }), _jsx(Typography.Title, { level: 4, style: { flex: 1, margin: 0 }, children: t('time_suggestions.title') }), _jsx(Button, { onClick: onClose, type: "text", icon: _jsx(FontAwesomeIcon, { icon: faXmark }) })] }), closeIcon: false, open: true, onClose: onClose, mask: false, size: COMPOSER_DRAWER_WIDTH, children: renderBody() }));
};
/**
 * Surfaces partial-result warnings: a clamped search window and/or calendars we
 * couldn't read (organizer failure vs. specific invitees).
 */
var PartialBanner = function (_a) {
    var partial = _a.partial, token = _a.token;
    var t = useTranslation('meetingRooms').t;
    if (!partial.isPartial) {
        return null;
    }
    var messages = [];
    if (partial.windowClamped) {
        messages.push(t('time_suggestions.partial.window_clamped'));
    }
    if (partial.organizerFetchFailed) {
        messages.push(t('time_suggestions.partial.organizer_fetch_failed'));
    }
    if (partial.failedInviteeEmails.length > 0) {
        messages.push(t('time_suggestions.partial.invitees_fetch_failed', {
            names: partial.failedInviteeEmails.join(', '),
        }));
    }
    if (messages.length === 0) {
        return null;
    }
    return (_jsx(Flex, { vertical: true, gap: 4, children: messages.map(function (message, index) { return (_jsx(Typography.Text, { style: { fontSize: 12, color: token.colorWarningText }, children: message }, index)); }) }));
};
var CenteredState = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  flex: 1;\n  padding: 32px 16px;\n  text-align: center;\n"], ["\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  flex: 1;\n  padding: 32px 16px;\n  text-align: center;\n"])));
var StyledDrawer = styled(Drawer)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  .ant-drawer-header-title {\n    width: 100%;\n  }\n\n  .ant-drawer-body {\n    padding: 0;\n    display: flex;\n    flex-direction: column;\n  }\n"], ["\n  .ant-drawer-header-title {\n    width: 100%;\n  }\n\n  .ant-drawer-body {\n    padding: 0;\n    display: flex;\n    flex-direction: column;\n  }\n"])));
var templateObject_1, templateObject_2;
